import { authorizedApi } from './index';

export interface ClaimPromotionalGiftResponse {
  message: string;
  serializedTransaction: string;
  gift: {
    [key: string]: any;
  };
}

export interface BroadcastClaimResponse {
  message: string;
  transactionId: string;
  gift: {
    [key: string]: any;
  };
}

export interface BroadcastClaimRequest {
  serializedTransaction: string;
}

export interface ClaimPromoGiftCodeResponse {
  message: string;
  gift: {
    [key: string]: any;
  };
}

export interface DoublePromoGiftCodeResponse {
  message: string;
}

export const authorizedPromotionalApi = authorizedApi
  .enhanceEndpoints({
    addTagTypes: ['PromotionalGift', 'Gifts'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      claimPromotionalGift: builder.mutation<
        ClaimPromotionalGiftResponse,
        number
      >({
        query: (giftId) => ({
          url: `/promotional/gift/${giftId}/claim`,
          method: 'POST',
        }),
        invalidatesTags: ['PromotionalGift', 'Gifts'],
        transformErrorResponse: (response: { status: number; data: any }) => {
          const errorMessages: { [key: number]: string } = {
            400: 'You need a Solana wallet to claim this gift.',
            401: 'Please log in to claim this gift.',
            404: 'Gift not found.',
            410: 'This gift has expired.',
            500: 'Failed to prepare claim transaction.',
          };
          return {
            status: response.status,
            message:
              errorMessages[response.status] ||
              response.data?.error ||
              'An error occurred',
          };
        },
      }),
      broadcastClaim: builder.mutation<
        BroadcastClaimResponse,
        { giftId: number; serializedTransaction: string }
      >({
        query: ({ giftId, serializedTransaction }) => ({
          url: `/promotional/gift/${giftId}/broadcast-claim`,
          method: 'POST',
          body: { serializedTransaction },
        }),
        invalidatesTags: ['PromotionalGift', 'Gifts'],
        transformErrorResponse: (response: { status: number; data: any }) => {
          const errorMessages: { [key: number]: string } = {
            400: response.data?.error || 'Failed to broadcast transaction.',
            401: 'Please log in to claim this gift.',
            403: 'You do not have access to this gift.',
            404: 'Gift not found.',
            500: 'Failed to broadcast transaction.',
          };
          return {
            status: response.status,
            message:
              errorMessages[response.status] ||
              response.data?.error ||
              'An error occurred',
            details: response.data?.details,
            transactionId: response.data?.transactionId,
          };
        },
      }),
      claimPromoGiftCode: builder.mutation<ClaimPromoGiftCodeResponse, string>({
        query: (promoGiftCode) => ({
          url: `/promotional/promo-code/${promoGiftCode}/claim`,
          method: 'POST',
        }),
        invalidatesTags: ['PromotionalGift', 'Gifts'],
        transformErrorResponse: (response: { status: number; data: any }) => {
          const errorMessages: { [key: number]: string } = {
            400: response.data?.error || 'Failed to claim promo gift code.',
            401: 'Please log in to claim this gift.',
            404: 'Promo gift code not found.',
            500: 'Failed to claim promo gift code.',
          };
          return {
            status: response.status,
            message:
              errorMessages[response.status] ||
              response.data?.error ||
              'An error occurred',
          };
        },
      }),
      doublePromoGiftCode: builder.mutation<
        DoublePromoGiftCodeResponse,
        string
      >({
        query: (promoGiftCode) => ({
          url: `/promotional/promo-code/${promoGiftCode}/double`,
          method: 'POST',
        }),
        invalidatesTags: ['PromotionalGift', 'Gifts'],
        transformErrorResponse: (response: { status: number; data: any }) => {
          const errorMessages: { [key: number]: string } = {
            400: response.data?.error || 'Failed to double promo gift code.',
            401: 'Please log in to double this promo code.',
            404: 'Promo gift code not found.',
            500: 'Failed to double promo gift code.',
          };
          return {
            status: response.status,
            message:
              errorMessages[response.status] ||
              response.data?.error ||
              'An error occurred',
          };
        },
      }),
    }),
    overrideExisting: false,
  });

export const {
  useClaimPromotionalGiftMutation,
  useBroadcastClaimMutation,
  useClaimPromoGiftCodeMutation,
  useDoublePromoGiftCodeMutation,
} = authorizedPromotionalApi;
